// src/pages/DiaryEdit.js — 일기 수정 페이지 (/diary/:diaryId/edit)
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { formatLocalDate, parseLocalDate, getUserInfo } from '../utils/storageUtils';
import { apiFetch } from '../utils/apiFetch';
import { DEFAULT_FONT_COLOR } from '../utils/fontColors';
import '../styles/DiaryEdit.css';
import { useToast } from '../components/Toast';

const DiaryEdit = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const { diaryId } = useParams();
    const myFontColor = getUserInfo()?.fontColor || DEFAULT_FONT_COLOR;

    // 페이지는 prop 대신 자체 조회로 폼을 초기화한다 — formData가 null이면 아직 로딩 전
    const [diary, setDiary] = useState(null);
    const [formData, setFormData] = useState(null);
    const [blocks, setBlocks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        // 라우트 가드 — 토큰 없이 직접 URL 진입 시 로그인으로
        if (!localStorage.getItem('accessToken')) {
            navigate('/login');
            return;
        }

        const load = async () => {
            try {
                const response = await apiFetch(`/diaries/${diaryId}`, { method: 'GET' });
                if (response.ok) {
                    const result = await response.json();
                    const d = result.data;
                    setDiary(d);
                    setFormData({
                        title: d.title || '',
                        date: d.date || formatLocalDate(new Date()),
                        openDate: d.openDate || ''
                    });
                    // 레거시(blocks 없음)면 본문 전체를 단일 블록으로
                    setBlocks(
                        d.blocks && d.blocks.length > 0
                            ? d.blocks.map(b => ({ ...b }))
                            : [{ blockId: null, fontColor: DEFAULT_FONT_COLOR, content: d.content || '' }]
                    );
                } else {
                    const errorData = await response.json().catch(() => null);
                    toast.error(errorData?.errorMessage || '일기를 불러오지 못했습니다.');
                    navigate('/diary', { replace: true });
                }
            } catch (error) {
                console.error('일기 조회 오류:', error);
                toast.error('네트워크 오류가 발생했습니다.');
                navigate('/diary', { replace: true });
            }
        };
        load();
        // toast는 렌더마다 새 객체라 의존성에서 제외 (Home.fetchCoupleInfo와 같은 예외)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [diaryId, navigate]);

    // 이미 개봉된 일기는 개봉일 변경 불가 (백엔드 D003과 동일 규칙 — 로컬 날짜 기준)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isOpened = Boolean(diary?.openDate) && parseLocalDate(diary.openDate) <= today;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // 입력 시 에러 메시지 클리어
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // 블록 textarea 높이를 내용에 맞춰 조절 (마운트 시 ref 콜백 + 입력 시)
    const autoResize = (el) => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    };

    const handleBlockChange = (index, value) => {
        setBlocks(prev => prev.map((block, i) =>
            i === index ? { ...block, content: value } : block
        ));
        if (errors.blocks) {
            setErrors(prev => ({ ...prev, blocks: '' }));
        }
    };

    // 이어쓰기 — 새 블록은 내 색으로 시작 (저장 시 서버가 내 귀속으로 기록)
    const handleAddBlock = () => {
        setBlocks(prev => [...prev, { blockId: null, fontColor: myFontColor, content: '' }]);
    };

    // 블록 순서 변경 — 인접 블록과 자리 교환. swap은 onChange를 타지 않아
    // autoResize가 못 따라오므로, 반영 후 모든 블록 높이를 다시 계산한다
    const handleMoveBlock = (index, direction) => {
        const target = index + direction;
        setBlocks(prev => {
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
        requestAnimationFrame(() => {
            document.querySelectorAll('.block-item .block-textarea').forEach(autoResize);
        });
    };

    const handleRemoveBlock = (index) => {
        if (blocks.length <= 1) return; // 최소 1블록
        setBlocks(prev => prev.filter((_, i) => i !== index));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = '제목을 입력해주세요.';
        } else if (formData.title.length > 100) {
            newErrors.title = '제목은 100자 이하로 입력해주세요.';
        }

        if (blocks.some(block => !block.content.trim())) {
            newErrors.blocks = '비어 있는 블록이 있습니다. 내용을 입력하거나 블록을 삭제해주세요.';
        } else if (blocks.some(block => block.content.length > 2000)) {
            newErrors.blocks = '각 블록은 2000자 이하로 입력해주세요.';
        }

        if (!formData.date) {
            newErrors.date = '일기 날짜를 선택해주세요.';
        } else {
            const selectedDate = parseLocalDate(formData.date);
            const today = new Date();
            today.setHours(23, 59, 59, 999);

            if (selectedDate > today) {
                newErrors.date = '미래 날짜는 선택할 수 없습니다.';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const response = await apiFetch(`/diaries/${diaryId}`, {
                method: 'PUT',
                body: JSON.stringify({
                    title: formData.title,
                    date: formData.date,
                    openDate: formData.openDate || null,
                    blocks: blocks.map(block => ({ blockId: block.blockId, content: block.content }))
                })
            });

            if (response.ok) {
                toast.success('일기가 수정되었습니다.');
                // 상세로 replace 복귀 — 상세가 마운트 시 재조회하므로 갱신 데이터 전달 불필요
                navigate(`/diary/${diaryId}`, { replace: true });
            } else {
                const errorData = await response.json();
                toast.error(errorData.errorMessage || errorData.message || '일기 수정에 실패했습니다.');
            }
        } catch (error) {
            console.error('일기 수정 오류:', error);
            toast.error('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
        } finally {
            setIsLoading(false);
        }
    };

    // 날짜 포맷팅 함수
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'short'
        });
    };

    if (!formData) {
        return (
            <div className="diary-edit-page">
                <AppHeader />
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="diary-edit-page">
            <AppHeader />
            <div className="diary-edit-panel">
                <div className="modal-header">
                    <h2>일기 수정</h2>
                    <button onClick={() => navigate(-1)} className="modal-close-btn">✕</button>
                </div>

                <div className="modal-content">

                    <form onSubmit={handleSubmit} className="diary-form">
                        <div className="form-group">
                            <label htmlFor="date" className="form-label">
                                일기 날짜 *
                            </label>
                            <div className="date-input-container">
                                <input
                                    type="date"
                                    id="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    className={`form-input date-input ${errors.date ? 'error' : ''}`}
                                    max={formatLocalDate(new Date())}
                                />
                                <div className="date-display">
                                    {formatDateForDisplay(formData.date)}
                                </div>
                            </div>
                            {errors.date && (
                                <span className="error-text">{errors.date}</span>
                            )}
                            <small className="form-hint">
                                일기 날짜를 선택하세요. (오늘까지만 선택 가능)
                            </small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="openDate" className="form-label">
                                🎁 타임캡슐 개봉일
                            </label>
                            <div className="date-input-container">
                                <input
                                    type="date"
                                    id="openDate"
                                    name="openDate"
                                    value={formData.openDate}
                                    disabled={isOpened}
                                    min={formatLocalDate(new Date(Date.now() + 24 * 60 * 60 * 1000))}
                                    onChange={handleInputChange}
                                    className="form-input date-input"
                                />
                            </div>
                            <small className="form-hint">
                                {isOpened
                                    ? '이미 개봉된 타임캡슐의 개봉일은 변경할 수 없습니다.'
                                    : '개봉 전까지 변경할 수 있습니다.'}
                            </small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="title" className="form-label">
                                제목 *
                            </label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                className={`form-input ${errors.title ? 'error' : ''}`}
                                placeholder="제목을 입력하세요"
                                maxLength={100}
                                autoFocus
                            />
                            {errors.title && (
                                <span className="error-text">{errors.title}</span>
                            )}
                            <small className="form-hint">
                                {formData.title.length}/100자
                            </small>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                내용 *
                            </label>
                            <div className="block-list">
                                {blocks.map((block, index) => (
                                    <div key={block.blockId ?? `new-${index}`} className="block-item">
                                        <textarea
                                            ref={autoResize}
                                            value={block.content}
                                            onChange={(e) => {
                                                handleBlockChange(index, e.target.value);
                                                autoResize(e.target);
                                            }}
                                            className={`form-textarea block-textarea ${errors.blocks ? 'error' : ''}`}
                                            style={{ color: block.fontColor, borderLeftColor: block.fontColor }}
                                            placeholder="일기 내용을 입력하세요..."
                                            rows={1}
                                            maxLength={2000}
                                        />
                                        {blocks.length > 1 && (
                                            <div className="block-controls">
                                                <button
                                                    type="button"
                                                    className="block-move-btn"
                                                    onClick={() => handleMoveBlock(index, -1)}
                                                    disabled={index === 0}
                                                    title="위로 이동"
                                                >
                                                    ▲
                                                </button>
                                                <button
                                                    type="button"
                                                    className="block-move-btn"
                                                    onClick={() => handleMoveBlock(index, 1)}
                                                    disabled={index === blocks.length - 1}
                                                    title="아래로 이동"
                                                >
                                                    ▼
                                                </button>
                                                <button
                                                    type="button"
                                                    className="block-remove-btn"
                                                    onClick={() => handleRemoveBlock(index)}
                                                    title="블록 삭제"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button type="button" className="add-block-btn" onClick={handleAddBlock}>
                                ✏️ 이어쓰기
                            </button>
                            {errors.blocks && (
                                <span className="error-text">{errors.blocks}</span>
                            )}
                            <small className="form-hint">
                                블록 내용을 고치면 그 블록은 내 색으로 바뀝니다. 이어쓰기로 내 블록을 추가하세요.
                            </small>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={() => navigate(-1)}
                            >
                                취소
                            </button>
                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="button-spinner"></div>
                                        수정 중...
                                    </>
                                ) : (
                                    '일기 수정'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default DiaryEdit;
